import type { Accessor } from 'solid-js';
import { useItemName } from './useItemName';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { parseItemKey } from '../services/craftingResolver';
import { fromIdentifier, fromItemStackDto } from '../services/itemRefAdapters';
import { customItemToStackDto } from '../components/inventory/inventoryUtils';
import type { CustomItemDefinition } from '../services/inventoryApi';
import type { ItemStack } from '../models/itemStack';

// A hashed item key resolves only through the tenant's own NBT registry, so the caller passes the
// definitions it has loaded.
export function useItemStacks(customItems: Accessor<CustomItemDefinition[]>) {
    const resolver = useCraftingResolver();
    const { itemName } = useItemName();

    const customStackFor = (key: string): ItemStack | undefined => {
        const parsed = parseItemKey(key);
        if (!parsed?.hash) return undefined;
        const definition = customItems().find(candidate =>
            candidate.hash === parsed.hash && candidate.itemId === parsed.id && candidate.metadata === parsed.metadata);
        return definition ? fromItemStackDto(customItemToStackDto(definition)) : undefined;
    };

    const nameFor = (key: string) => itemName(key, customStackFor(key)?.snbt);

    const itemStack = (key: string) => {
        const custom = customStackFor(key);
        if (custom) return custom;
        const def = resolver()?.getAllItems()?.find(i => i.key === key);
        return fromIdentifier(def ? `minecraft:${def.name}` : key, def?.metadata ?? 0, resolver());
    };

    return { customStackFor, nameFor, itemStack };
}
